/**
 * Deep-link return for the standalone redirect auth flow.
 *
 * The Cognito redirect always lands on the registered redirect_uri (the
 * origin root), so an agent who opened /support unauthenticated would be
 * dumped on the chat page after sign-in. The path is remembered here before
 * `signinRedirect` and restored by an in-router `navigate` once the user is
 * authenticated (see AutoSignin).
 *
 * Why sessionStorage and not the OIDC `state` parameter: by the time
 * react-oidc-context's onSigninCallback runs, BrowserRouter has already
 * mounted at "/", and react-router does not observe a raw
 * `history.replaceState`, so a state-based restore would leave the router
 * rendering the chat page under a /support URL. sessionStorage survives the
 * same-tab redirect round trip and lets a component inside the router do a
 * real navigation.
 */

/** sessionStorage key carrying the path to restore after the redirect round trip. */
export const RETURN_TO_KEY = "auth:returnTo"

/**
 * Whether a stored value is a safe in-app path to navigate to.
 *
 * Same-origin storage is not attacker-controlled in this app's model, but the
 * guard is cheap: only absolute in-app paths pass, never protocol-relative
 * ("//host") or full URLs, so a corrupted value cannot become a redirect off
 * the origin.
 *
 * @param path The candidate value.
 * @returns True only for "/..." paths that are not "//...".
 */
export function isSafeReturnPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//")
}

/**
 * Remember the current in-app path before starting a redirect sign-in.
 *
 * @param storage The Storage to persist into (window.sessionStorage in prod).
 * @param path The path (plus query) to return to after sign-in.
 */
export function rememberReturnTo(storage: Storage, path: string): void {
  if (isSafeReturnPath(path)) storage.setItem(RETURN_TO_KEY, path)
}

/**
 * Read and clear the remembered return path. One-shot on purpose: a stale
 * value must never redirect a later, unrelated sign-in.
 *
 * @param storage The Storage to read from (window.sessionStorage in prod).
 * @returns The safe path to restore, or null when absent or unsafe.
 */
export function consumeReturnTo(storage: Storage): string | null {
  const stored = storage.getItem(RETURN_TO_KEY)
  storage.removeItem(RETURN_TO_KEY)
  return stored !== null && isSafeReturnPath(stored) ? stored : null
}
